import { useEffect, useMemo, useState } from 'react';
import { trim } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';

import type { AssetType } from 'src/data/asset';
import {
  DEFAULT_SCORING_METHOD,
  mapScoringMethodForMutation,
  type ScoringMethod,
} from 'src/data/scoring';
import { parseHistogramData, type Variation } from 'src/data/variation';
import { scoreCopyText, scoreLongFormAsset } from 'src/graphql/scoring';
import { isAbortError } from 'src/lib/errors';
import { captureMessage } from 'src/lib/sentry';
// @ts-expect-error - project store has no TypeScript declarations
import { useCurrentProjectId } from 'src/store/project/useCurrentProjectId';
import type { BackendMultiScoreSuggestionType } from 'src/types/backend';

import { LONG_FORM_INSIGHTS_DEBOUNCE_TIMEOUT } from './longFormConst';

interface Params {
  assetType?: AssetType | null;
  text: string;
  html: string;
  /** The scoring method the caller holds; the backend's answer supersedes it through `onScored`. */
  getScoringMethod: () => ScoringMethod | null | undefined;
  onScored?: (scoringMethod: ScoringMethod, secondaryScoringMethod?: ScoringMethod | null) => void;
  /** Bumped by the caller to force a re-score of text that has not changed. */
  resetCounter?: number;
  /** An Editor long-form row, scored by its own mutation against its card's brief. */
  longFormAssetId?: string;
}

export const useLongFormScoringAndHistogramsData = ({
  assetType,
  text,
  html,
  getScoringMethod,
  onScored,
  resetCounter,
  longFormAssetId,
}: Params) => {
  const [scoringData, setScoringData] = useState<BackendMultiScoreSuggestionType | null>(null);
  const [loading, setLoading] = useState(false);

  const projectId = useCurrentProjectId();

  const fetchScoringData = useDebouncedCallback(async (requestParams, requestOptions) => {
    try {
      const { longFormAssetId: assetId, ...copyTextParams } = requestParams;
      // An Editor long-form row has its own mutation, which scores it against its card's brief.
      const result = assetId
        ? await scoreLongFormAsset(
            {
              longFormAssetId: assetId,
              text: copyTextParams.text,
              html: copyTextParams.html,
              scoringMethod: copyTextParams.scoringMethod,
            },
            requestOptions
          )
        : await scoreCopyText(copyTextParams, requestOptions);
      setScoringData(result ?? null);

      // The backend knows better which scoring method the text was measured against.
      onScored?.(
        (result?.scoringMethod as ScoringMethod) || DEFAULT_SCORING_METHOD,
        result?.secondaryScoringMethod as ScoringMethod
      );
    } catch (error) {
      if (isAbortError(error as Error)) {
        return;
      }

      captureMessage('Long form: fetching score and histogram data', {
        level: 'warning',
        extra: { error: String(error), assetType, text },
      });
    } finally {
      setLoading(false);
    }
  }, LONG_FORM_INSIGHTS_DEBOUNCE_TIMEOUT);

  useEffect(() => {
    if (!projectId || trim(text).length === 0) {
      setScoringData(null);
      return;
    }

    // Loading starts while the debounce is still pending, so the score does not sit on a value the
    // user has already typed past.
    setLoading(true);

    const abortController = new AbortController();
    fetchScoringData(
      {
        projectId,
        assetType,
        text,
        html,
        scoringMethod: mapScoringMethodForMutation(getScoringMethod() ?? DEFAULT_SCORING_METHOD),
        longFormAssetId,
      },
      { abortSignal: abortController.signal }
    );

    return () => {
      abortController.abort();
      setLoading(false);
    };
  }, [
    fetchScoringData,
    assetType,
    resetCounter,
    projectId,
    text,
    html,
    getScoringMethod,
    longFormAssetId,
  ]);

  return useMemo(() => {
    const { score, engagementScoreSupportSize, isEnterpriseScoreUsed } = scoringData || {};
    // Only the two histogram fields are read, which a scored text carries like a variation does.
    const parsedHistograms = parseHistogramData((scoringData ?? {}) as Variation);

    return {
      scoringData: {
        score,
        basedOn: engagementScoreSupportSize,
        isEnterpriseScoreUsed,
        loading,
      },
      histogramsData: {
        age: parsedHistograms?.ages,
        gender: parsedHistograms?.genders,
        loading,
      },
    };
  }, [scoringData, loading]);
};
